import {
    BellOutlined,
    CloseOutlined,
    EyeOutlined,
    FileOutlined,
    MoreOutlined,
    PushpinOutlined
} from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import { InternetRecourceType } from '@entities/channel/model/types'
import { MessageView } from '@entities/post'
import { checkDraftPost } from '@entities/post/api'
import { basePostType } from '@shared/lib/constants/constants'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import type { FormProps } from 'antd'
import { Button, Col, Divider, Dropdown, Flex, Form, Row, Select, Space, theme, Typography } from 'antd'
import { Input, Popup } from 'antd-mobile'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { usePosts } from '../../model/hooks/use-posts'
import { MessageType, type Message, type PostState } from '../../model/types'
import * as styles from './add-post-mobile-modal.module.scss'

const { Text } = Typography

interface AddPostModalProps {
    open: boolean
    sessionId: string
    onClose: () => void
}

type FieldType = {
    title: string
    state: PostState
    channels: number[]
    publishDate: Dayjs
    publishTime: Dayjs
    deleteDate: Dayjs
    notify: boolean
    isPinned: boolean
    messages: Message[]
    postType: {
        value: number
    }
}

export const AddPostMobileModal = ({ open, sessionId, onClose }: AddPostModalProps) => {
    const { channels, currentChannel, channelDetails } = useChannels()

    const {
        actions: { createPost, previewPost },
        postTypes
    } = usePosts({ channelId: currentChannel?.id })

    const {
        token: { colorPrimary, colorWhite }
    } = theme.useToken()

    const [form] = Form.useForm<FieldType>()

    const text = channelDetails?.settings?.general.showSignature
        ? `</br></br>${channelDetails?.settings?.general.signature}`
        : ''

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async (values: FieldType) => {
            const dateStr = values.publishDate // '2025-06-13'
            const timeStr = values.publishTime // '14:30'

            const [hour, minute] = timeStr.split(':').map(Number)

            const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

            createPost({
                ...values,
                publishDate: publishDate.toDate()
            })

            onClose()
        },
        [createPost, onClose]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const values = form.getFieldsValue()

    const moreActionsItems = useMemo(
        () => [
            {
                key: '1',
                label: (
                    <Space style={{ width: '100%' }}>
                        <FileOutlined />В черновики
                    </Space>
                ),
                onClick: () => onSubmit()
            },
            {
                key: '3',
                label: (
                    <Space style={{ width: '100%' }}>
                        <EyeOutlined />
                        Предпросмотр в бот
                    </Space>
                ),
                onClick: () => {
                    const dateStr = values.publishDate // '2025-06-13'
                    const timeStr = values.publishTime // '14:30'

                    const [hour, minute] = timeStr.split(':').map(Number)

                    const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

                    previewPost({
                        ...values,
                        publishDate: publishDate?.toDate()
                    })
                }
            }
        ],
        [onSubmit, previewPost, values]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const channelsToDisplay = useMemo(
        () =>
            channels
                ?.filter((c) => c.type === InternetRecourceType.channel)
                .map((c) => ({ label: c.name, value: c.id })),
        [channels]
    )

    useEffect(() => {
        if (!sessionId) return
        checkDraftPost(sessionId).then((postData) => {
            if (postData) {
                form.setFieldsValue({
                    messages: [
                        {
                            ...postData.post,
                            id: Date.now(),
                            file: postData.post.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        }
                    ]
                })
            }
        })
    }, [form, sessionId])

    const navigate = useNavigate()

    useEffect(() => {
        if (currentChannel) {
            form.setFieldValue('channels', currentChannel?.id ? [currentChannel?.id] : [])
        }
    }, [currentChannel, form])

    return (
        <Popup visible={open} onMaskClick={onClose} onClose={onClose} bodyStyle={{ background: '#F0F3F5' }}>
            <Form
                form={form}
                name="add-post"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                onFinish={onFinish}
                initialValues={{
                    state: 'draft',
                    notify: true,
                    publishDate: dayjs().format('YYYY-MM-DD'),
                    publishTime: dayjs().format('HH:mm'),
                    messages: [
                        {
                            file: [],
                            id: Date.now(),
                            messageType: MessageType.PHOTO,
                            multi: true,
                            isCaptionAbove: false,
                            text: text
                        }
                    ]
                }}
                style={{ padding: '20px 16px', maxHeight: '100vh', overflow: 'auto' }}
            >
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row gutter={[15, 15]}>
                                <Col span={24}>
                                    <Flex gap={6}>
                                        <CustomTag color={basePostType.color}>{basePostType.name}</CustomTag>
                                        <Form.Item<FieldType>
                                            name="postType"
                                            required={false}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Выберите тип"
                                                labelInValue
                                                optionLabelProp="label"
                                                popupMatchSelectWidth={false}
                                                popupRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <div style={{ padding: '8px', textAlign: 'center' }}>
                                                            <Button
                                                                type="primary"
                                                                onClick={() => navigate('/settings')}
                                                            >
                                                                + Новый тип
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            >
                                                {postTypes?.map((item) => (
                                                    <Select.Option
                                                        style={{ textAlign: 'center' }}
                                                        key={item.id}
                                                        value={item.id}
                                                        label={item.name}
                                                    >
                                                        <CustomTag color={item.color}>{item.name}</CustomTag>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        name="channels"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            showSearch
                                            mode="multiple"
                                            optionFilterProp="label"
                                            style={{ width: '100%' }}
                                            options={channelsToDisplay}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Text style={{ whiteSpace: 'nowrap' }}>Опубликовать</Text>
                            <Flex gap={8} align="center" style={{ marginTop: '10px' }}>
                                <div className={styles.inputCard}>
                                    <Form.Item<FieldType>
                                        name="publishDate"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0, marginLeft: 'auto' }}
                                    >
                                        <Input type="date" placeholder="Дата публикации" style={{ width: '120px' }} />
                                    </Form.Item>
                                </div>
                                <div className={styles.inputCard}>
                                    <Form.Item<FieldType>
                                        name="publishTime"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Input type="time" placeholder="Время публикации" style={{ width: '82px' }} />
                                    </Form.Item>
                                </div>
                            </Flex>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Flex align="center">
                                <Text style={{ whiteSpace: 'nowrap' }}>Закреп/Уведомления</Text>
                                <Form.Item<FieldType>
                                    name="isPinned"
                                    required={false}
                                    style={{ marginLeft: 'auto', marginBottom: 0 }}
                                >
                                    <IconSwitch text={'Закрепить'}>
                                        <PushpinOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                                <Form.Item<FieldType> name="notify" required={false} style={{ marginBottom: 0 }}>
                                    <IconSwitch text={'Отправить со звуком'}>
                                        <BellOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                            </Flex>
                        </div>
                    </Col>
                    {values.messages?.length && (
                        <Col span={24}>
                            <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                                {values.messages.map((item) => (
                                    <MessageView message={item} key={item.id} />
                                ))}
                            </div>
                        </Col>
                    )}

                    <Col span={24}>
                        <Flex gap={6}>
                            <Button type="primary" onClick={onClose} size="large">
                                <CloseOutlined />
                            </Button>
                            <Button
                                type="primary"
                                style={{ flex: 1 }}
                                onClick={() => onSubmit({ state: 'planned' })}
                                size="large"
                            >
                                Запланировать
                            </Button>
                            <Dropdown
                                key="more"
                                trigger={['click']}
                                menu={{
                                    items: moreActionsItems
                                }}
                            >
                                <Button type="primary" style={{ verticalAlign: 'bottom' }} size="large">
                                    <MoreOutlined style={{ color: colorWhite }} />
                                </Button>
                            </Dropdown>
                        </Flex>
                    </Col>
                </Row>

                <Form.Item<FieldType> name="state" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="messages" hidden />
                <Form.Item<FieldType> label="Введите название поста" name="title" hidden initialValue={'Title'}>
                    <Input />
                </Form.Item>
            </Form>
        </Popup>
    )
}
